// Memory storage fallback for when localStorage is not available
class MemoryStorage {
  private storage: Map<string, string> = new Map()

  getItem(key: string): string | null {
    return this.storage.get(key) || null
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value)
  }

  removeItem(key: string): void {
    this.storage.delete(key)
  }

  clear(): void {
    this.storage.clear()
  }
}

// Test if localStorage is available
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__localStorage_test__'
    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

// Create storage instance with fallback
const memoryStorage = new MemoryStorage()
const storageAvailable = isLocalStorageAvailable()

export const useStorageWithFallback = {
  get(key: string): string | null {
    try {
      if (storageAvailable) {
        return localStorage.getItem(key)
      }
      return memoryStorage.getItem(key)
    } catch {
      return memoryStorage.getItem(key)
    }
  },

  set(key: string, value: string): void {
    try {
      if (storageAvailable) {
        localStorage.setItem(key, value)
        return
      }
      memoryStorage.setItem(key, value)
    } catch {
      memoryStorage.setItem(key, value)
    }
  },

  remove(key: string): void {
    try {
      if (storageAvailable) {
        localStorage.removeItem(key)
        return
      }
      memoryStorage.removeItem(key)
    } catch {
      memoryStorage.removeItem(key)
    }
  },

  clear(): void {
    try {
      if (storageAvailable) {
        localStorage.clear()
        return
      }
      memoryStorage.clear()
    } catch {
      memoryStorage.clear()
    }
  }
}
